import { computed, inject, signal, Service } from '@angular/core';
import { firstValueFrom, from, Observable, of } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';

import { TranslationsService } from './translations.service';
import { preloadLocalTranslations } from '../data/translations.dictionary';
import { PlatformService } from '@core/platform/platform.service';
import { StorageService } from '@core/storage/storage.service';
import { StorageKeys } from '@core/storage/storage.keys';
import { LanguageService } from '@core/language/language.service';

import {
  DEFAULT_LANGUAGE,
  directionOf,
  fallbackLanguageOf,
  Language,
  TextDirection,
} from '../domain/language';
import { isI18nKey, TranslationMap } from '../domain/translation.types';

/**
 * TranslationFacade — the signals-first public API for v2 i18n (refactored
 * copy of the v1 TranslationFacade). Components/services depend ONLY on this.
 *
 * State is held in signals; `currentTranslations` drives the TranslatePipe.
 * `initialize()` is what the Splash screen awaits — the splash "loading"
 * literally depends on this completing.
 *
 * Provided at the application ROOT (via `provideI18n` in `app.config.ts`) so the
 * whole app — root-scoped services included — shares one facade instance.
 */
@Service({ autoProvided: false })
export class TranslationFacade {
  private readonly service = inject(TranslationsService);
  private readonly platform = inject(PlatformService);
  private readonly storage = inject(StorageService);
  private readonly language = inject(LanguageService);

  // ─── signals (state) ─────────────────────────────────────────────────
  private readonly _current = signal<TranslationMap>({});
  private readonly _fallback = signal<TranslationMap>({});
  private readonly _language = signal<Language>(this.readStoredLanguage());
  private readonly _loading = signal<boolean>(false);
  private readonly _initialized = signal<boolean>(false);

  // ─── signals (public, read-only computed) ────────────────────────────
  readonly translations = computed(() => this._current());
  readonly currentLanguage = computed(() => this._language());
  readonly direction = computed<TextDirection>(() => directionOf(this._language()));
  protected readonly isLoading = computed(() => this._loading());
  protected readonly isInitialized = computed(() => this._initialized());

  private inFlight: Promise<TranslationMap> | null = null;

  constructor() {
    // Seed with the bundled local dictionary so UI labels (e.g. the splash
    // brand text) render correctly INSTANTLY — before the async response.
    // `initialize()` still gates readiness on the real (mock/API) load.
    this._current.set(this.service.getLocalTranslations(this._language()));
  }

  // ─── INIT ────────────────────────────────────────────────────────────
  /**
   * Load translations for `lang` (or the stored/current language) and publish
   * them to the signals. Returns an Observable so callers can react to
   * completion; `isInitialized()` flips true on success.
   */
  initialize(lang?: Language): Observable<TranslationMap> {
    const target = lang ?? this._language();

    // ── SSR: resolve from the BUNDLED dictionary, never the API ───────────────
    //
    // This is the ONE awaited step of the startup sequence (see
    // `AppInitializationService`), so on the server it decides whether a render can
    // complete at all. `GET /api/translations` goes through raw HttpClient — not
    // `AppHttp`, whose server gate would otherwise cover it — and off-prod the API is
    // unreachable from the render host, so awaiting it hangs the render until the
    // connect timeout and then rejects with the aborted fetch. That is precisely the
    // failure that forced the whole redesign to be client-rendered.
    //
    // The bundled dictionary (`translations.dictionary.ts`) already carries the
    // complete ar + en copy and already seeds this facade synchronously in the
    // constructor, so the server has everything it needs with ZERO network: the
    // server-rendered `<head>` gets real localized title/description copy, startup
    // completes immediately, and the render cannot hang or crash.
    //
    // The browser still fetches the live dictionary as before, so a server-side copy
    // edit is picked up the moment the client's own load lands.
    if (this.platform.isServer) {
      const local = this.service.getLocalTranslations(target);
      this._current.set(local);
      this._language.set(target);
      this._initialized.set(true);
      this.loadFallback();
      return of(local);
    }

    this._loading.set(true);

    // Ensure `target`'s local dictionary chunk is in memory before the merge
    // pipeline (mergeWithLocal/patchWithLocalKeys) reads it synchronously — a
    // no-op await for the FIRST load (bootstrap already preloaded this exact
    // language, see src/main.ts), and for a language SWITCH it's covered by
    // the same `_loading` spinner the API call itself already shows, so this
    // adds no new visible state, just a slightly longer existing one.
    //
    // No force-bypass: a language switch HITS the language-scoped cache when it
    // exists (no API call) and only fetches on a miss — per the caching spec.
    return from(preloadLocalTranslations(target)).pipe(
      switchMap(() => this.service.getTranslations(target)),
      tap((data) => {
        this._current.set(data);
        this._language.set(target);
        this._initialized.set(true);
        this.loadFallback();
      }),
      finalize(() => this._loading.set(false)),
    );
  }

  /**
   * Idempotent init — used by the Home route resolver so deep-links work even
   * if the user never passed through the splash. Resolves once translations
   * are available.
   */
  ensureInitialized(): Promise<TranslationMap> {
    if (this._initialized()) return Promise.resolve(this._current());
    if (this.inFlight) return this.inFlight;
    this.inFlight = firstValueFrom(this.initialize()).finally(() => (this.inFlight = null));
    return this.inFlight;
  }

  // ─── SET / CHANGE LANGUAGE ────────────────────────────────────────────
  setLanguage(lang: Language): Observable<TranslationMap> {
    if (this.platform.isBrowser) this.storage.set(StorageKeys.LANGUAGE, lang);
    return this.initialize(lang);
  }

  /**
   * Public language-switch API (spec name): persist the target language, then
   * load its translations (cache hit ⇒ no API call; miss ⇒ fetch + merge +
   * cache under the language-specific key). Alias of {@link setLanguage}.
   */
  protected changeLanguage(language: Language): Observable<TranslationMap> {
    return this.setLanguage(language);
  }

  protected toggleLanguage(): Observable<TranslationMap> {
    return this.setLanguage(fallbackLanguageOf(this._language()));
  }

  // ─── TRANSLATE ────────────────────────────────────────────────────────
  /** Resolve a flattened key (`a_b_c`); falls back to the other language, then a humanised key. */
  translate(key: string): string {
    // Defensive: a null/empty key (e.g. an unmapped data field bound via `| t`)
    // must never throw — return an empty string instead of crashing the view.
    if (!key) return '';
    const current = this._current()[key];
    if (current) return current;

    const fallback = this._fallback()[key];
    if (fallback) return fallback;

    return this.humanise(key);
  }

  /** Short alias used in templates via the TranslatePipe. */
  t(key: string): string {
    return this.translate(key);
  }

  /**
   * Resolve a value that MIGHT be a raw i18n key OR already-localized prose. A
   * snake_case key is translated against the live (API-merged) dictionary;
   * anything with spaces / capitals / punctuation / Arabic is already prose and
   * returned verbatim (never humanise-mangled). Use for text from a source that
   * is inconsistent about keys-vs-prose — e.g. an API `message` surfaced in a
   * toast — so a stray key never renders raw to the user.
   */
  resolveMessage(value: string | null | undefined): string {
    if (!value) return '';
    return isI18nKey(value) ? this.translate(value) : value;
  }

  has(key: string): boolean {
    return !!this._current()[key];
  }

  // ─── internals ────────────────────────────────────────────────────────
  private loadFallback(): void {
    const fb = fallbackLanguageOf(this._language());
    if (Object.keys(this._fallback()).length > 0) return;
    // The fallback language's dictionary is its own lazy chunk (rev. 9 perf
    // pass — see translations.dictionary.ts): load it in the background, off
    // the critical path (the PRIMARY language is already showing by the time
    // this runs). Until it resolves, translate()'s cross-language fallback
    // lookup simply misses — same graceful "not found yet" behavior a slow
    // API response already produces, never a crash or wrong value.
    void preloadLocalTranslations(fb).then(() => {
      this._fallback.set(this.service.getLocalTranslations(fb));
    });
  }

  /**
   * The language this facade starts in — delegated to {@link LanguageService},
   * the one place that knows how a language is resolved on each platform.
   *
   * It used to read `StorageKeys.LANGUAGE` directly and hard-return
   * `DEFAULT_LANGUAGE` on the server. In the browser that was equivalent to
   * `LanguageService.load()` (same key, same `toLanguage` coercion), so nothing
   * changes there. On the SERVER it was the reason a server render could not be
   * English: `LanguageService` now resolves the request's `?lang=` / the
   * visitor's cookie, but this facade — which is what every `translate()` call,
   * and therefore every localized `<title>`/description/OG value, actually reads
   * — ignored that and stayed Arabic. Two resolvers disagreeing about one
   * request is the bug; there is now one.
   */
  private readStoredLanguage(): Language {
    return this.language.load() ?? DEFAULT_LANGUAGE;
  }

  private humanise(key: string): string {
    if (!key) return '';
    // Title-Case each word: `reference_portal_home` → "Reference Portal Home".
    return key
      .replace(/_/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
